<template>
    <tab-layout>
        <template #title>
            <span class="tab-routing-addresses__title">
                {{ translations.title }}
                <CompassPill
                    :label="translations.facturationElectroniquePill"
                    :color="CompassEnums.PillColor.ORANGE"
                    :variant="CompassEnums.PillVariant.PASTEL"
                    :size="CompassEnums.PillSize.EXTRA_SMALL"
                />
            </span>
        </template>
        <template #content>
            <RoutingAddressesLoading v-if="loading" />
            <RoutingAddressesEmptySiren v-else-if="displayState === 'siren-missing'" />
            <RoutingAddressesEmptyNotDetected v-else-if="displayState === 'not-detected'" />
            <RoutingAddressesList
                v-else
                :addresses="routingAddresses"
                @setDefault="setDefault"
            />
        </template>
    </tab-layout>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue';
import TabLayout from 'vcomponents/overview/tabs/TabLayout.vue';
import RoutingAddressesLoading from 'vcomponents/overview/tabs/routingAddresses/RoutingAddressesLoading.vue';
import RoutingAddressesEmptySiren from 'vcomponents/overview/tabs/routingAddresses/RoutingAddressesEmptySiren.vue';
import RoutingAddressesEmptyNotDetected from 'vcomponents/overview/tabs/routingAddresses/RoutingAddressesEmptyNotDetected.vue';
import RoutingAddressesList from 'vcomponents/overview/tabs/routingAddresses/RoutingAddressesList.vue';
import { fetchRoutingAddresses, MOCK_DISPLAY_OVERRIDE } from 'api/routingAddressesService';
import type { Translator } from 'js/shims-vue';
import type { RoutingAddress } from 'types/entities/einvoicing/routingAddress.type';

type DisplayState = 'siren-missing' | 'not-detected' | 'has-addresses';

const $t = inject('$t') as Translator;
const translations: Record<string, string> = {
    title: $t.__('overview.tabs.TabRoutingAddresses.title'),
    facturationElectroniquePill: $t.__(
        'overview.tabs.TabRoutingAddresses.facturation_electronique_pill',
    ),
};

const loading = ref<boolean>(true);
const routingAddresses = ref<RoutingAddress[]>([]);

const displayState = computed<DisplayState>(() => {
    if (MOCK_DISPLAY_OVERRIDE === 'siren-missing') {
        return 'siren-missing';
    }
    if (MOCK_DISPLAY_OVERRIDE === 'not-detected') {
        return 'not-detected';
    }
    // TODO (SAAS-32515) : 'auto': mocked data drives the rendering. When the backend lands and the override is
    // removed, re-introduce the real SIREN check here (read `siren` from the overview store
    // via `useStore().getters.dataFetch.informations`) and return 'siren-missing' when absent.
    return routingAddresses.value.length === 0 ? 'not-detected' : 'has-addresses';
});

const fetch = async (): Promise<void> => {
    try {
        loading.value = true;
        routingAddresses.value = await fetchRoutingAddresses();
    } finally {
        loading.value = false;
    }
};

const setDefault = (id: number): void => {
    // Local-only mock: exactly one address is `isDefault`. Activating one deactivates the others.
    // TODO(SAAS-32324): replace with a real API call when the backend lands.
    routingAddresses.value = routingAddresses.value.map((address) => ({
        ...address,
        isDefault: address.id === id,
    }));
};

onMounted(() => {
    fetch();
});
</script>

<style lang="scss" scoped>
.tab-routing-addresses {
    &__title {
        align-items: center;
        display: inline-flex;
        gap: var(--spacing-2);
    }
}
</style>
